import { useState, useRef, useCallback, useEffect } from "react";
import { database } from "../firebase";
import {
    ref,
    set,
    serverTimestamp,
    onValue,
    off,
} from "firebase/database";
import { Html5Qrcode } from "html5-qrcode";
import { loadAllowedDomains, isAllowedScannedUrl } from "../utils/scanner/domainValidators";
import { getLocation } from "../utils/scanner/locationUtils";

const useScanner = ({ user, onStopScan, setUniqueKey, setUserID }) => {
    const [message, setMessage] = useState("");
    const [instructions, setInstructions] = useState("");
    const [showRetry, setShowRetry] = useState(false);
    const [redundantScanInfo, setRedundantScanInfo] = useState(null);

    const qrReaderRef = useRef(null);
    const isProcessingRef = useRef(false);

    const allowedList = loadAllowedDomains();

    /* ---------- Scanner Cleanup ---------- */

    const stopAndClearScanner = async () => {
        if (!qrReaderRef.current) return;

        try {
            await qrReaderRef.current.stop?.();
            await qrReaderRef.current.clear?.();
        } catch (err) {
            console.warn("Error stopping scanner:", err);
        } finally {
            qrReaderRef.current = null;
        }
    };

    /* ---------- Redundant Scan Handler ---------- */

    const handleRedundantScan = async (scanID, userPath, duplicateScan) => {
        await stopAndClearScanner();

        const targetUserPath = duplicateScan ? duplicateScan.userID : userPath;
        const targetScanID = duplicateScan ? duplicateScan.scanID : scanID;
        const isOtherUser = targetUserPath !== userPath;

        // Use backend-provided completeness flags
        const needsAddress = !isOtherUser && duplicateScan?.needsAddress;
        const needsPhoto = !isOtherUser && duplicateScan?.needsPhoto;
        const isComplete = isOtherUser || duplicateScan?.isComplete;

        setMessage("QR Code Already Scanned");

        if (!isComplete && (needsAddress || needsPhoto)) {
            setInstructions(
                "This QR code has already been scanned, but it's incomplete.\n\n" +
                "Complete the missing steps below:"
            );
        } else {
            setInstructions(
                "This QR code cannot be scanned from this location as a QR code from this campaign has already been scanned here.\n\n" +
                "Please scan a different QR code."
            );
        }

        setShowRetry(true);
        setRedundantScanInfo({
            scanKey: targetScanID,
            userPath: targetUserPath,
            needsAddress: needsAddress,
            needsPhoto: needsPhoto
        });
    };

    /* ---------- Save Temp Scan & Wait ---------- */

    const saveTempScanAndWait = async (
        decodedText,
        latitude,
        longitude,
        accuracy
    ) => {
        const userPath =
            user?.email?.replaceAll(".", "-") || "unknown-user";

        const scanID =
            decodedText.replaceAll(/[^a-zA-Z0-9_-]/g, "-");

        const tempRef = ref(
            database,
            `data/temp/${userPath}/${scanID}`
        );

        const statusRef = ref(
            database,
            `data/temp/${userPath}/${scanID}`
        );

        setMessage("Verifying...");
        setInstructions("");
        setShowRetry(false);

        await set(tempRef, {
            qrcodeData: decodedText,
            coordinates: { x: latitude, y: longitude, acc: accuracy },
            createTimestamp: serverTimestamp(),
            tempData: false,
        });

        // Return a cleanup function so the component can detach the listener if unmounted
        let cleanup = () => { };

        const promise = new Promise((resolve) => {
            const listener = onValue(statusRef, async (snap) => {
                const val = snap.val();
                if (!val) return;

                const { redundant, redundantLoc, duplicateScan, error, validationFailed } = val;

                // Handle backend verification errors
                if (error || validationFailed) {
                    off(statusRef, "value", listener);
                    setMessage("Verification Failed");
                    setInstructions(error || "An error occurred during verification.");
                    setShowRetry(true);
                    resolve();
                    return;
                }

                // Wait until backend finishes both checks
                if (
                    redundant === undefined ||
                    redundantLoc === undefined
                ) {
                    return;
                }

                off(statusRef, "value", listener);

                /* ---------- FINAL DECISION ---------- */

                // Block in both cases:
                // 1. redundant === true
                // 2. redundant === false && redundantLoc === true
                if (redundant === true || redundantLoc === true) {
                    await handleRedundantScan(scanID, userPath, duplicateScan);
                    resolve();
                    return;
                }

                // Allow only when both false
                if (redundant === false && redundantLoc === false) {
                    setUniqueKey?.(scanID);
                    setUserID?.(userPath);
                    onStopScan?.();
                    resolve();
                }
            });

            cleanup = () => off(statusRef, "value", listener);
        });

        return promise;
    };

    /* ---------- QR Handler ---------- */

    const handleDecodedText = async (decodedText) => {
        if (isProcessingRef.current) return;
        isProcessingRef.current = true;

        try {
            if (!isAllowedScannedUrl(decodedText, allowedList)) {
                await stopAndClearScanner();
                setMessage("Invalid QR Code");
                setInstructions("Please scan a valid QR code.");
                setShowRetry(true);
                return;
            }

            if (navigator.vibrate) {
                try {
                    navigator.vibrate(200);
                } catch (e) {
                    console.error("Vibration failed", e);
                }
            }

            const position = await getLocation();
            await stopAndClearScanner();

            await saveTempScanAndWait(
                decodedText,
                position.coords.latitude,
                position.coords.longitude,
                position.coords.accuracy
            );
        } catch (err) {
            await stopAndClearScanner();

            if (err?.code === 2) {
                setMessage("Location Permission Blocked");
                setInstructions(
                    "1. Tap the 🔒 Lock icon in your browser address bar.\n" +
                    "2. Open Permissions or Location settings.\n" +
                    "3. Allow location access.\n" +
                    "4. Then retry."
                );
                setShowRetry(true);
                return;
            }

            console.error("Scanner error:", err);
            setMessage("Camera / Location Permission Blocked");
            setInstructions(
                "1. Tap the 🔒 Lock icon in your browser address bar.\n" +
                "2. Open Permissions or Location settings.\n" +
                "3. Allow required permissions.\n" +
                "4. Then retry."
            );
            setShowRetry(true);
        } finally {
            isProcessingRef.current = false;
        }
    };

    /* ---------- Start Scanner ---------- */

    const startScanner = useCallback(async () => {
        try {
            setMessage("");
            setInstructions("");
            setShowRetry(false);
            setRedundantScanInfo(null);
            isProcessingRef.current = false;

            await stopAndClearScanner();

            const qrReader = new Html5Qrcode("reader");
            qrReaderRef.current = qrReader;

            await qrReader.start(
                { facingMode: "environment" },
                { fps: 10, qrbox: { width: 400, height: 400 } },
                handleDecodedText
            );
        } catch (err) {
            console.error("Scanner start error:", err);
            setMessage("Camera / Location Permission Blocked");
            setInstructions(
                "1. Tap the 🔒 Lock icon in your browser address bar.\n" +
                "2. Open Permissions or Location settings.\n" +
                "3. Allow required permissions.\n" +
                "4. Then retry."
            );
            setShowRetry(true);
        }
    }, []);

    /* ---------- Lifecycle ---------- */

    useEffect(() => {
        startScanner();
        return () => stopAndClearScanner();
    }, [startScanner]);

    return {
        message,
        instructions,
        showRetry,
        startScanner,
        redundantScanInfo,
    };
};

export default useScanner;
